import React, { useState, useEffect, createContext, useContext } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Phone, Mail, Facebook, MessageCircle, Leaf, Menu, X, Globe } from 'lucide-react'; // Added Globe icon

// --- Language Context and Translations ---

interface Translations {
  [key: string]: {
    [key: string]: string;
  };
}

const translations: Translations = {
  en: {
    home: "Home",
    menu: "Menu",
    contact: "Contact",
    rvWebsiteTitle: "RVTHAIFISH.COM", // Changed key name for clarity
    byRvImportExport: "By R.V. Import & Export Co.,Ltd.",
    ornamentalFishExporter: "We are the ornamental fish exporter. We supply many kinds of quality fish with reasonable price and always serve all customers the best!",
    readMore: "Read More",
    fishListImportExport: "Fish List, Import & Export",
    allFish: "All Fish",
    discus: "Discus",
    goldFish: "Gold Fish",
    bettaFish: "Betta Fish",
    angelFish: "Angel Fish",
    addCard: "+ Add Card",
    viewMore: "View More...",
    globalShippingExportServices: "Global Shipping & Export Services",
    shippingDescription: "We ensure your ornamental fish are delivered safely and efficiently worldwide. Our expert team handles all aspects of import and export, from meticulous packaging to navigating international regulations.",
    contactUs: "Contact Us",
    followUs: "Follow us",
    allRightsReserved: "All rights reserved.",
    fishNotFound: "Fish Not Found",
    fishNotFoundMessage: "The fish you are looking for does not exist.",
    backToMenu: "Back to Menu",
    aboutUs: "About Us",
    aboutUsDescription: "We are R.V. Import & Export Co.,Ltd., a leading ornamental fish exporter committed to providing high-quality fish with reasonable prices. Our mission is to serve all customers the best.",
    phone: "+66 890685262",
    email: "rv.export.co@gmail.com",
    facebook: "facebook.com/rv.thaifish",
    whatsapp: "whatapp/rv.thaifish",
    addressLine1: "105,81 Soi 21 Lam Phak Kut, Thanyaburi District,",
    addressLine2: "Pathum Thani 12110",
    companyName: "R.V. Import & Export Co., Ltd.",
    discusPompadour1Long: "This stunning Discus Pompadour 1 is a vibrant and healthy specimen, showcasing the classic round body shape and intricate patterns characteristic of the Discus species. Known as the 'King of the Aquarium', Discus fish are renowned for their majestic appearance and graceful movements. This fish is bred for optimal health and color, making it an excellent choice for experienced aquarists looking to add a touch of elegance to their tanks. Discus prefer warm, soft, acidic water and a stable environment for thriving.",
    discusPompadour2Long: "A highly colorful Discus Pompadour 2, this fish displays a brilliant palette of reds, blues, and yellows, making it a captivating centerpiece for any freshwater aquarium. Discus are sensitive to water quality, requiring consistent temperatures and pristine conditions. They are intelligent and form strong social bonds within their groups, so it's recommended to keep them in schools of at least 5-6 individuals. This fish is acclimated to a diverse diet, ensuring its continued vibrancy and growth in your home aquarium.",
    discusPompadour3Long: "This healthy Discus Pompadour 3 is robust and active, an ideal candidate for shipment to discerning aquarists worldwide. Its solid coloration and well-proportioned body reflect meticulous care and breeding. Discus fish demand attention to detail in their environment, including soft, acidic water, regular water changes, and a stable temperature around 82-86°F (28-30°C). This fish is eating well and showing excellent signs of vitality, ready to adapt to a new, carefully prepared aquarium.",
    discusPompadour4Long: "Experience the premium quality of Discus Pompadour 4, offered at a special price for a limited time. This fish exhibits superior genetic traits, including intense coloration and a flawless body shape. Discus are known to be shy but can become quite interactive once comfortable in their surroundings. They appreciate a calm environment with plenty of hiding spots and soft lighting. This particular specimen is a fantastic opportunity for collectors to acquire a top-tier Discus at an exceptional value.",
    discusPompadour5Long: "This premium quality Discus Pompadour 5 is a testament to expert breeding, featuring rich, deep hues and striking patterns that will instantly elevate your aquarium's aesthetics. Discus fish thrive on a high-protein diet, and this specimen is accustomed to a variety of nutritious foods, ensuring vibrant health and color. Their unique parental care behavior, where parents secrete a nutritious slime coat for their fry, makes them fascinating to observe for dedicated aquarists.",
    discusPompadour6Long: "Introducing Discus Pompadour 6, a rare and sought-after variant within the Discus family. This fish exhibits unique markings and color combinations, making it a prized possession for serious collectors. Maintaining rare Discus requires precise water parameters and a keen eye for their well-being. This specimen has been carefully quarantined and is free of common ailments, ready to integrate into a specialized Discus setup. Don't miss this opportunity to own a truly exceptional fish.",
    discusPompadour7Long: "A truly vibrant Discus Pompadour 7, this fish radiates health and vitality with its brilliant colors and active demeanor. Ideal for a well-maintained community tank, provided tank mates are peaceful and share similar water requirements. Discus are fascinating fish that can learn to recognize their owner and even eat from their hand over time. This fish promises to bring dynamic beauty and captivating behavior to your aquatic display.",
    discusPompadour8Long: "While Discus are known for being demanding, this Discus Pompadour 8 is a hardy and adaptable strain, making it comparatively 'easy-to-care' for those new to the species, yet still demanding good husbandry. It exhibits robust health and consistent coloration. These Discus have been raised in conditions that promote resilience, making them a great starting point for aspiring Discus keepers who are committed to learning their unique needs. Proper diet and stable water conditions remain key to their long-term health.",
    discusPompadour9Long: "This Discus Pompadour 9 is of export-quality, meaning it meets the highest standards for health, color, and finnage, ensuring it will be a spectacular addition to any collection worldwide. Each fish undergoes stringent health checks and careful conditioning before export to guarantee its well-being during transit and upon arrival. Our commitment to quality ensures that you receive a beautiful and robust fish that will thrive in its new home.",
    discusPompadour10Long: "For the discerning collector, Discus Pompadour 10 represents a pinnacle of aesthetic perfection and genetic purity. This magnificent specimen features rare color combinations and exceptional fin development, making it a highly valuable addition to any high-end aquarium. This fish is a true showstopper, embodying the elegance and challenge that experienced Discus enthusiasts cherish. Secure this exquisite piece for your collection today.",
    orandaGoldFish1Long: "Oranda Gold Fish, known for its distinctive raspberry-like hood (wen) on its head, is a popular fancy goldfish variety. This particular Oranda has a vibrant orange coloration and a well-developed, symmetrical wen. They are generally peaceful and can grow quite large, making them suitable for spacious aquariums. Orandas prefer cool, well-filtered water and thrive on a varied diet of high-quality flakes, pellets, and live or frozen foods. Their docile nature makes them excellent community fish with other non-aggressive, similarly sized goldfish.",
    orandaGoldFish2Long: "This radiant golden Oranda Gold Fish exhibits a stunning metallic sheen and graceful swimming patterns. Its large, flowing fins add to its elegant appearance. Orandas are social fish and do well in groups of three or more, provided the tank is large enough to accommodate their adult size. They are susceptible to swim bladder issues if overfed or fed low-quality food, so a balanced diet is crucial. With proper care, Orandas can live for 10-15 years, becoming long-term beloved pets.",
    orandaGoldFish3Long: "A uniquely patterned Oranda Gold Fish, this specimen boasts an intricate mix of colors and a distinct wen formation, setting it apart from others. Each fish has its own unique charm, making them a favorite among aquarists. Orandas require a robust filtration system due to their waste production and prefer a dimly lit environment to prevent stress. Regular water changes and monitoring of water parameters are essential for their health and vibrant coloration. This particular Oranda is a show-quality fish, perfect for enthusiasts looking for a standout addition.",
    bettaSplendens1Long: "This vibrant Betta Splendens, also known as the Siamese Fighting Fish, displays an stunning array of colors and elegant, flowing fins. Betta fish are well-known for their captivating beauty and distinct personalities. This particular specimen is a male, ideal for a single tank setup as they can be aggressive towards other male Bettas. They thrive in warm, filtered water with plenty of hiding spots and require a varied diet to maintain their brilliant coloration and health. Betta fish make wonderful pets due to their interactive nature and relatively easy care.",
    bettaSplendens2Long: "A graceful Betta Splendens with striking blue and red colors, this fish is a true aquatic jewel. Its delicate finnage and vibrant hues create a mesmerizing display in any aquarium. Bettas are labyrinth fish, meaning they can breathe atmospheric oxygen, allowing them to survive in low-oxygen environments, though they prefer well-oxygenated water. They are curious and active, often interacting with their surroundings and even their owner. Provide a comfortable habitat with gentle filtration and a balanced diet for optimal health and color.",
    bettaSplendens3Long: "This elegant Betta Splendens is a perfect centerpiece for your aquarium, with its elaborate fin structure and captivating movements. Bettas are carnivorous and should be fed high-quality betta pellets, frozen, or live foods like bloodworms and brine shrimp. They enjoy a planted tank environment with gentle water flow. Their territorial nature means careful consideration is needed for tank mates, though many do well in peaceful community tanks with appropriate species. This fish will bring a unique character to your aquatic display.",
    bettaSplendens4Long: "Feast your eyes on this beautifully patterned Betta Splendens, featuring unique finnage and a mesmerizing blend of colors. This fish is a testament to selective breeding, resulting in a truly exceptional specimen. Bettas require a minimum of a 5-gallon tank to thrive, as smaller bowls can lead to health issues. They are sensitive to ammonia and nitrite, so proper cycling and regular water changes are essential. This Betta will provide endless fascination with its intricate patterns and dynamic presence.",
    angelFish1Long: "This graceful Angel Fish, with its elegant, flowing fins and striking vertical stripes, is a perfect addition to a well-planted freshwater aquarium. Angelfish are cichlids native to South America and are known for their distinctive triangular body shape. They are generally peaceful but can be territorial, especially during breeding. They thrive in tanks with tall plants and open swimming spaces. A varied diet of flakes, pellets, and frozen foods will keep them healthy and vibrant. This beautiful fish adds a serene and majestic presence to any aquatic setup.",
    angelFish2Long: "A stunning Angel Fish that adds an immediate sense of serenity and elegance to any aquarium. This specimen exhibits beautiful fin development and a calm demeanor. Angelfish prefer clean, well-oxygenated water with a neutral to slightly acidic pH. They are schooling fish and do best when kept in groups of at least 3-5, which helps reduce individual stress. Their elongated fins make them susceptible to fin nipping, so choosing compatible tank mates is crucial. This is a robust and healthy fish, ready to integrate into your community tank.",
    angelFish3Long: "This beautifully striped Angel Fish is a classic ornamental fish, beloved for its distinctive markings and graceful swimming. Its bold stripes provide excellent camouflage in planted aquariums. Angelfish require ample vertical space due to their body shape and enjoy a diet rich in protein. They are known for their intelligence and can often be seen interacting with their environment and even their owners. This particular Angel Fish is a high-quality specimen, perfect for both beginner and experienced aquarists looking to add a touch of timeless beauty to their tank.",
  },
  th: {
    home: "หน้าแรก",
    menu: "เมนู",
    contact: "ติดต่อ",
    rvWebsiteTitle: "อาร์.วี.ไทยฟิช.คอม", // Changed key name for clarity
    byRvImportExport: "โดย บริษัท อาร์.วี. อิมพอร์ต แอนด์ เอ็กซ์พอร์ต จำกัด",
    ornamentalFishExporter: "เราคือผู้ส่งออกปลาสวยงาม เราจัดหาปลาคุณภาพหลากหลายชนิดในราคาที่สมเหตุสมผล และให้บริการลูกค้าทุกท่านอย่างดีที่สุดเสมอ!",
    readMore: "อ่านเพิ่มเติม",
    fishListImportExport: "รายการปลา, นำเข้าและส่งออก",
    allFish: "ปลาทั้งหมด",
    discus: "ปลาปอมปาดัวร์",
    goldFish: "ปลาทอง",
    bettaFish: "ปลากัด",
    angelFish: "ปลาเทวดา",
    addCard: "+ เพิ่มลงตะกร้า",
    viewMore: "ดูเพิ่มเติม...",
    globalShippingExportServices: "บริการจัดส่งและส่งออกทั่วโลก",
    shippingDescription: "เรามั่นใจว่าปลาสวยงามของคุณจะถูกจัดส่งอย่างปลอดภัยและมีประสิทธิภาพทั่วโลก ทีมผู้เชี่ยวชาญของเราจัดการทุกด้านของการนำเข้าและส่งออก ตั้งแต่การบรรจุหีบห่ออย่างพิถีพิถันไปจนถึงการปฏิบัติตามกฎระเบียบระหว่างประเทศ",
    contactUs: "ติดต่อเรา",
    followUs: "ติดตามเรา",
    allRightsReserved: "สงวนลิขสิทธิ์",
    fishNotFound: "ไม่พบปลา",
    fishNotFoundMessage: "ไม่พบปลาที่คุณกำลังค้นหา",
    backToMenu: "กลับไปที่เมนู",
    aboutUs: "เกี่ยวกับเรา",
    aboutUsDescription: "เราคือ บริษัท อาร์.วี. อิมพอร์ต แอนด์ เอ็กซ์พอร์ต จำกัด ผู้ส่งออกปลาสวยงามชั้นนำ มุ่งมั่นที่จะจัดหาปลาคุณภาพสูงในราคาที่สมเหตุสมผล ภารกิจของเราคือการให้บริการลูกค้าทุกท่านอย่างดีที่สุด",
    phone: "โทรศัพท์: +66 890685262",
    email: "อีเมล: rv.export.co@gmail.com",
    facebook: "เฟซบุ๊ก: facebook.com/rv.thaifish",
    whatsapp: "วอทส์แอพ: whatapp/rv.thaifish",
    addressLine1: "105,81 ซอย 21 ลำผักกูด, อำเภอธัญบุรี,",
    addressLine2: "ปทุมธานี 12110",
    companyName: "บริษัท อาร์.วี. อิมพอร์ต แอนด์ เอ็กซ์พอร์ต จำกัด",
    discusPompadour1Long: "ปลาปอมปาดัวร์ 1 ตัวนี้เป็นปลาที่สวยงามและแข็งแรง แสดงให้เห็นถึงรูปร่างกลมคลาสสิกและลวดลายที่ซับซ้อนอันเป็นเอกลักษณ์ของสายพันธุ์ปลาปอมปาดัวร์ ปลาปอมปาดัวร์เป็นที่รู้จักในฐานะ 'ราชาแห่งตู้ปลา' มีชื่อเสียงในด้านรูปลักษณ์ที่สง่างามและการเคลื่อนไหวที่สง่างาม ปลาตัวนี้ถูกเพาะพันธุ์มาเพื่อสุขภาพและสีสันที่ดีที่สุด ทำให้เป็นตัวเลือกที่ยอดเยี่ยมสำหรับนักเลี้ยงปลาที่มีประสบการณ์ที่ต้องการเพิ่มความสง่างามให้กับตู้ปลาของตน ปลาปอมปาดัวร์ชอบน้ำอุ่น นุ่ม มีสภาพเป็นกรดเล็กน้อย และสภาพแวดล้อมที่มั่นคงเพื่อการเจริญเติบโต",
    discusPompadัว2Long: "ปลาปอมปาดัวร์ 2 ตัวนี้มีสีสันสดใส แสดงให้เห็นถึงเฉดสีแดง น้ำเงิน และเหลืองที่สดใส ทำให้เป็นจุดเด่นที่น่าหลงใหลสำหรับตู้ปลาน้ำจืดทุกประเภท ปลาปอมปาดัวร์อ่อนไหวต่อคุณภาพน้ำ ต้องการอุณหภูมิที่คงที่และสภาพที่บริสุทธิ์ พวกมันฉลาดและสร้างความผูกพันทางสังคมที่แข็งแกร่งภายในกลุ่ม ดังนั้นจึงแนะนำให้เลี้ยงพวกมันเป็นฝูงอย่างน้อย 5-6 ตัว ปลาตัวนี้ปรับตัวเข้ากับอาหารที่หลากหลาย ทำให้มั่นใจได้ถึงความมีชีวิตชีวาและการเจริญเติบโตอย่างต่อเนื่องในตู้ปลาของคุณ",
    discusPompadัว3Long: "ปลาปอมปาดัวร์ 3 ตัวนี้แข็งแรงและกระตือรือร้น เป็นตัวเลือกที่เหมาะสำหรับการจัดส่งไปยังนักเลี้ยงปลาที่พิถีพิถันทั่วโลก สีสันที่แข็งแกร่งและรูปร่างที่สมส่วนสะท้อนถึงการดูแลและการเพาะพันธุ์ที่พิถีพิถัน ปลาปอมปาดัวร์ต้องการความใส่ใจในรายละเอียดของสภาพแวดล้อม รวมถึงน้ำอ่อน มีสภาพเป็นกรดเล็กน้อย การเปลี่ยนน้ำเป็นประจำ และอุณหภูมิที่คงที่ประมาณ 82-86°F (28-30°C) ปลาตัวนี้กินอาหารได้ดีและแสดงสัญญาณที่ดีเยี่ยมของความมีชีวิตชีวา พร้อมที่จะปรับตัวเข้ากับตู้ปลาใหม่ที่เตรียมไว้อย่างระมัดระวัง",
    discusPompadัว4Long: "สัมผัสประสบการณ์ปลาปอมปาดัวร์ 4 คุณภาพพรีเมียม เสนอในราคาพิเศษสำหรับช่วงเวลาจำกัด ปลาตัวนี้แสดงให้เห็นถึงลักษณะทางพันธุกรรมที่เหนือกว่า รวมถึงสีสันที่เข้มข้นและรูปร่างที่ไร้ที่ติ ปลาปอมปาดัวร์เป็นที่รู้จักกันว่าขี้อาย แต่สามารถโต้ตอบได้ดีเมื่อคุ้นเคยกับสภาพแวดล้อม พวกมันชอบสภาพแวดล้อมที่สงบพร้อมจุดซ่อนตัวที่เพียงพอและแสงสว่างที่นุ่มนวล ปลาตัวนี้เป็นโอกาสที่ยอดเยี่ยมสำหรับนักสะสมที่จะได้ปลาปอมปาดัวร์ชั้นนำในราคาที่คุ้มค่าเป็นพิเศษ",
    discusPompadัว5Long: "ปลาปอมปาดัวร์ 5 คุณภาพพรีเมียมนี้เป็นเครื่องพิสูจน์ถึงความเชี่ยวชาญในการเพาะพันธุ์ มีสีสันที่เข้มข้นและลวดลายที่โดดเด่นซึ่งจะยกระดับความสวยงามของตู้ปลาของคุณทันที ปลาปอมปาดัวร์เจริญเติบโตได้ดีด้วยอาหารโปรตีนสูง และปลาตัวนี้คุ้นเคยกับอาหารที่มีคุณค่าทางโภชนาการที่หลากหลาย ทำให้มั่นใจได้ถึงสุขภาพและสีสันที่สดใส พฤติกรรมการดูแลลูกปลาที่ไม่เหมือนใคร โดยที่พ่อแม่จะหลั่งเมือกเคลือบที่อุดมด้วยสารอาหารสำหรับลูกปลา ทำให้พวกมันน่าสนใจในการสังเกตสำหรับนักเลี้ยงปลาที่ทุ่มเท",
    discusPompadัว6Long: "ขอแนะนำปลาปอมปาดัวร์ 6 ซึ่งเป็นปลาปอมปาดัวร์ที่หายากและเป็นที่ต้องการในตระกูลปลาปอมปาดัวร์ ปลาตัวนี้แสดงให้เห็นถึงลวดลายและการผสมสีที่เป็นเอกลักษณ์ ทำให้เป็นทรัพย์สินที่มีค่าสำหรับนักสะสมที่จริงจัง การดูแลปลาปอมปาดัวร์ที่หายากต้องใช้พารามิเตอร์น้ำที่แม่นยำและสายตาที่เฉียบคมเพื่อสุขภาพของพวกมัน ปลาตัวนี้ถูกกักกันอย่างระมัดระวังและปราศจากโรคทั่วไป พร้อมที่จะรวมเข้ากับตู้ปลาปอมปาดัวร์ที่เชี่ยวชาญ อย่าพลาดโอกาสนี้ที่จะได้เป็นเจ้าของปลาที่ยอดเยี่ยมอย่างแท้จริง",
    discusPompadัว7Long: "ปลาปอมปาดัวร์ 7 ตัวนี้มีชีวิตชีวาอย่างแท้จริง แผ่รังสีสุขภาพและความมีชีวิตชีวาด้วยสีสันที่สดใสและพฤติกรรมที่กระตือรือร้น เหมาะสำหรับตู้ปลาชุมชนที่ได้รับการดูแลอย่างดี โดยมีเงื่อนไขว่าเพื่อนร่วมตู้ปลาต้องสงบและมีความต้องการน้ำที่คล้ายกัน ปลาปอมปาดัวร์เป็นปลาที่น่าสนใจที่สามารถเรียนรู้ที่จะจดจำเจ้าของและแม้กระทั่งกินอาหารจากมือเมื่อเวลาผ่านไป ปลาตัวนี้สัญญาว่าจะนำความงามอันโดดเด่นและพฤติกรรมที่น่าหลงใหลมาสู่ตู้ปลาของคุณ",
    discusPompadัว8Long: "แม้ว่าปลาปอมปาดัวร์จะขึ้นชื่อเรื่องความต้องการสูง แต่ปลาปอมปาดัวร์ 8 ตัวนี้เป็นสายพันธุ์ที่แข็งแรงและปรับตัวได้ ทำให้ 'ดูแลรักษาง่าย' สำหรับผู้ที่เพิ่งเริ่มเลี้ยงสายพันธุ์นี้ แต่ยังคงต้องการการดูแลที่ดี แสดงให้เห็นถึงสุขภาพที่แข็งแรงและสีสันที่สม่ำเสมอ ปลาปอมปาดัวร์เหล่านี้ได้รับการเลี้ยงดูในสภาพที่ส่งเสริมความยืดหยุ่น ทำให้เป็นจุดเริ่มต้นที่ดีสำหรับผู้ที่ต้องการเลี้ยงปลาปอมปาดัวร์ที่มุ่งมั่นที่จะเรียนรู้ความต้องการเฉพาะของพวกมัน การให้อาหารที่เหมาะสมและสภาพน้ำที่มั่นคงยังคงเป็นกุญแจสำคัญต่อสุขภาพที่ดีในระยะยาวของพวกมัน",
    discusPompadัว9Long: "ปลาปอมปาดัวร์ 9 ตัวนี้มีคุณภาพการส่งออก ซึ่งหมายความว่ามันตรงตามมาตรฐานสูงสุดด้านสุขภาพ สีสัน และครีบ ทำให้มั่นใจได้ว่าจะเป็นส่วนเสริมที่งดงามสำหรับคอลเลกชันทั่วโลก ปลาแต่ละตัวผ่านการตรวจสอบสุขภาพอย่างเข้มงวดและปรับสภาพอย่างระมัดระวังก่อนการส่งออก เพื่อรับประกันสุขภาพที่ดีระหว่างการขนส่งและเมื่อมาถึง ความมุ่งมั่นของเราในด้านคุณภาพทำให้มั่นใจได้ว่าคุณจะได้รับปลาที่สวยงามและแข็งแรงที่จะเจริญเติบโตในบ้านใหม่",
    discusPompadัว10Long: "สำหรับนักสะสมที่พิถีพิถัน ปลาปอมปาดัวร์ 10 เป็นจุดสูงสุดของความสมบูรณ์แบบทางสุนทรียศาสตร์และความบริสุทธิ์ทางพันธุกรรม ปลาตัวนี้มีสีสันที่หายากและการพัฒนาครีบที่ยอดเยี่ยม ทำให้เป็นส่วนเสริมที่มีค่าอย่างยิ่งสำหรับตู้ปลาไฮเอนด์ทุกประเภท ปลาตัวนี้เป็นจุดเด่นที่แท้จริง สะท้อนถึงความสง่างามและความท้าทายที่ผู้ที่ชื่นชอบปลาปอมปาดัวร์ที่มีประสบการณ์ให้ความสำคัญ รับชิ้นส่วนที่ประณีตนี้สำหรับคอลเลกชันของคุณวันนี้",
    orandaGoldFish1Long: "ปลาทองออรันดา ซึ่งเป็นที่รู้จักจากส่วนหัวที่เป็นเอกลักษณ์ (wen) คล้ายผลราสเบอร์รี่ เป็นปลาทองสายพันธุ์แฟนซีที่ได้รับความนิยม ปลาทองออรันดาตัวนี้มีสีส้มสดใสและมีส่วนหัวที่พัฒนาอย่างดีและสมมาตร โดยทั่วไปแล้วพวกมันจะสงบและสามารถเติบโตได้ค่อนข้างใหญ่ ทำให้เหมาะสำหรับตู้ปลาขนาดกว้างขวาง ปลาทองออรันดาชอบน้ำเย็นที่กรองอย่างดี และเจริญเติบโตได้ดีด้วยอาหารเม็ด อาหารเม็ดคุณภาพสูง และอาหารสดหรือแช่แข็งที่หลากหลาย ลักษณะนิสัยที่สงบของพวกมันทำให้เป็นปลาที่ยอดเยี่ยมสำหรับตู้ปลาชุมชนร่วมกับปลาทองอื่นๆ ที่ไม่ก้าวร้าวและมีขนาดใกล้เคียงกัน",
    orandaGoldFish2Long: "ปลาทองออรันดาที่มีสีทองสดใสตัวนี้แสดงให้เห็นถึงความเงางามของโลหะที่น่าทึ่งและรูปแบบการว่ายน้ำที่สง่างาม ครีบที่ใหญ่และพลิ้วไหวของมันเพิ่มความสง่างามให้กับรูปลักษณ์ ปลาทองออรันดาเป็นปลาสังคมและทำได้ดีเมื่อเลี้ยงเป็นกลุ่มสามตัวขึ้นไป โดยมีเงื่อนไขว่าตู้ปลาจะต้องมีขนาดใหญ่พอที่จะรองรับขนาดตัวเต็มวัยของพวกมันได้ พวกมันเสี่ยงต่อปัญหาถุงลมถ้าให้อาหารมากเกินไปหรือให้อาหารคุณภาพต่ำ ดังนั้นอาหารที่สมดุลจึงเป็นสิ่งสำคัญ ด้วยการดูแลที่เหมาะสม ปลาทองออรันดาสามารถมีชีวิตอยู่ได้ 10-15 ปี กลายเป็นสัตว์เลี้ยงที่รักใคร่ยาวนาน",
    orandaGoldFish3Long: "ปลาทองออรันดาที่มีลวดลายที่เป็นเอกลักษณ์ตัวนี้มีสีสันที่ผสมผสานกันอย่างซับซ้อนและส่วนหัวที่โดดเด่น ทำให้แตกต่างจากตัวอื่น ปลาแต่ละตัวมีเสน่ห์เฉพาะตัว ทำให้เป็นที่ชื่นชอบในหมู่นักเลี้ยงปลา ปลาทองออรันดาต้องการระบบกรองที่แข็งแกร่งเนื่องจากการผลิตของเสียของพวกมัน และชอบสภาพแวดล้อมที่มีแสงน้อยเพื่อป้องกันความเครียด การเปลี่ยนน้ำเป็นประจำและการตรวจสอบพารามิเตอร์น้ำเป็นสิ่งสำคัญสำหรับสุขภาพและสีสันที่สดใสของพวกมัน ปลาทองออรันดาตัวนี้เป็นปลาคุณภาพสูง เหมาะสำหรับผู้ที่ชื่นชอบที่กำลังมองหาสิ่งพิเศษมาเพิ่มเติม",
    bettaSplendens1Long: "ปลากัด 1 ตัวนี้ หรือที่รู้จักกันในชื่อปลากัดสยาม แสดงให้เห็นถึงสีสันที่น่าทึ่งและครีบที่พลิ้วไหวอย่างสง่างาม ปลากัดมีชื่อเสียงในด้านความงามที่น่าหลงใหลและบุคลิกที่โดดเด่น ปลากัดตัวผู้ตัวนี้เหมาะสำหรับตู้ปลาเดี่ยว เนื่องจากพวกมันอาจก้าวร้าวต่อปลากัดตัวผู้ตัวอื่น พวกมันเจริญเติบโตได้ดีในน้ำอุ่นที่กรองแล้ว มีจุดซ่อนตัวมากมาย และต้องการอาหารที่หลากหลายเพื่อรักษาสีสันสดใสและสุขภาพที่ดี ปลากัดเป็นสัตว์เลี้ยงที่ยอดเยี่ยมเนื่องจากธรรมชาติที่โต้ตอบได้ง่ายและการดูแลที่ค่อนข้างง่าย",
    bettaSplendens2Long: "ปลากัด 2 ตัวนี้สง่างาม มีสีน้ำเงินและแดงที่โดดเด่น เป็นอัญมณีแห่งสัตว์น้ำอย่างแท้จริง ครีบที่ละเอียดอ่อนและสีสันสดใสสร้างการแสดงผลที่น่าหลงใหลในตู้ปลาทุกประเภท ปลากัดเป็นปลาที่มีอวัยวะพิเศษที่ช่วยให้หายใจออกซิเจนในบรรยากาศได้ ทำให้พวกมันสามารถอยู่รอดได้ใน low-oxygen environments, though they prefer well-oxygenated water. They are curious and active, often interacting with their surroundings and even their owner. Provide a comfortable habitat with gentle filtration and a balanced diet for optimal health and color.",
    bettaSplendens3Long: "ปลากัด 3 ตัวนี้สง่างาม เป็นจุดเด่นที่สมบูรณ์แบบสำหรับตู้ปลาของคุณ ด้วยโครงสร้างครีบที่ประณีตและการเคลื่อนไหวที่น่าหลงใหล ปลากัดเป็นสัตว์กินเนื้อและควรได้รับอาหารเม็ดปลากัดคุณภาพสูง อาหารแช่แข็ง หรืออาหารสด เช่น หนอนแดงและไรทะเล พวกมันชอบสภาพแวดล้อมในตู้ปลาที่มีพืชน้ำและการไหลเวียนของน้ำที่อ่อนโยน ลักษณะนิสัยอาณาเขตของพวกมันหมายความว่าต้องพิจารณาอย่างรอบคอบสำหรับเพื่อนร่วมตู้ปลา แม้ว่าหลายตัวจะเข้ากันได้ดีในตู้ปลาชุมชนที่สงบกับสายพันธุ์ที่เหมาะสมก็ตาม ปลาตัวนี้จะนำลักษณะเฉพาะที่น่าสนใจมาสู่การจัดแสดงสัตว์น้ำของคุณ",
    bettaSplendens4Long: "ดื่มด่ำกับความงามของปลากัด 4 ตัวนี้ ซึ่งมีลวดลายที่เป็นเอกลักษณ์และครีบที่น่าหลงใหลพร้อมการผสมผสานของสีสันที่น่าตื่นตาตื่นใจ ปลาตัวนี้เป็นเครื่องพิสูจน์ถึงการเพาะพันธุ์แบบคัดเลือก ส่งผลให้ได้ปลาที่ยอดเยี่ยมอย่างแท้จริง ปลากัดต้องการตู้ปลาอย่างน้อย 5 แกลลอนเพื่อการเจริญเติบโต เนื่องจากตู้ปลาขนาดเล็กอาจนำไปสู่ปัญหาสุขภาพได้ พวกมันอ่อนไหวต่อแอมโมเนียและไนไตรท์ ดังนั้นการหมุนเวียนน้ำที่เหมาะสมและการเปลี่ยนน้ำเป็นประจำจึงเป็นสิ่งสำคัญ ปลากัดตัวนี้จะมอบความเพลิดเพลินไม่รู้จบด้วยลวดลายที่ซับซ้อนและการปรากฏตัวที่กระตือรือร้น",
    angelFish1Long: "ปลาเทวดา 1 ตัวนี้สง่างาม ด้วยครีบที่พลิ้วไหวอย่างสง่างามและลายเส้นแนวตั้งที่โดดเด่น เป็นส่วนเสริมที่สมบูรณ์แบบสำหรับตู้ปลาน้ำจืดที่มีพืชน้ำหนาแน่น ปลาเทวดาเป็นปลาหมอสีพื้นเมืองในอเมริกาใต้ และเป็นที่รู้จักจากรูปร่างตัวสามเหลี่ยมที่โดดเด่น โดยทั่วไปพวกมันจะสงบ แต่สามารถแสดงพฤติกรรมหวงถิ่นได้ โดยเฉพาะอย่างยิ่งในช่วงการผสมพันธุ์ พวกมันเจริญเติบโตได้ดีในตู้ปลาที่มีพืชสูงและพื้นที่ว่ายน้ำเปิด อาหารที่หลากหลายทั้งอาหารเม็ด อาหารเม็ด และอาหารแช่แข็งจะช่วยให้พวกมันมีสุขภาพดีและมีชีวิตชีวา ปลาที่สวยงามตัวนี้นำความสงบและความสง่างามมาสู่การจัดแสดงสัตว์น้ำทุกประเภท",
    angelFish2Long: "ปลาเทวดา 2 ตัวนี้สวยงามน่าทึ่ง นำความสงบและความสง่างามมาสู่ตู้ปลาทันที ปลาตัวนี้แสดงให้เห็นถึงการพัฒนาครีบที่สวยงามและบุคลิกที่สงบ ปลาเทวดาชอบน้ำสะอาดที่มีออกซิเจนดี และมีค่า pH เป็นกลางถึงกรดเล็กน้อย พวกมันเป็นปลาฝูงและจะทำได้ดีที่สุดเมื่อเลี้ยงเป็นกลุ่มอย่างน้อย 3-5 ตัว ซึ่งช่วยลดความเครียดของแต่ละตัว ครีบที่ยาวของพวกมันทำให้เสี่ยงต่อการถูกกัดครีบ ดังนั้นการเลือกเพื่อนร่วมตู้ปลาที่เข้ากันได้จึงเป็นสิ่งสำคัญ ปลาตัวนี้แข็งแรงและมีสุขภาพดี พร้อมที่จะรวมเข้ากับตู้ปลาชุมชนของคุณ",
    angelFish3Long: "ปลาเทวดา 3 ตัวนี้มีลายเส้นที่สวยงาม เป็นปลาสวยงามคลาสสิกที่ได้รับความนิยมจากเครื่องหมายที่โดดเด่นและการว่ายน้ำที่สง่างาม ลายเส้นที่เข้มข้นของมันช่วยในการพรางตัวได้ดีเยี่ยมในตู้ปลาที่มีพืชน้ำ ปลาเทวดาต้องการพื้นที่แนวตั้งที่เพียงพอเนื่องจากรูปร่างตัวของพวกมัน และชอบอาหารที่มีโปรตีนสูง พวกมันเป็นที่รู้จักในด้านความฉลาดและมักจะถูกพบเห็นว่าโต้ตอบกับสภาพแวดล้อมและแม้กระทั่งเจ้าของของพวกมัน ปลาเทวดาตัวนี้เป็นปลาคุณภาพสูง เหมาะสำหรับทั้งนักเลี้ยงปลาเริ่มต้นและนักเลี้ยงปลาที่มีประสบการณ์ที่ต้องการเพิ่มความงามเหนือกาลเวลาให้กับตู้ปลาของตน",
  },
};

type Language = 'en' | 'th';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const t = (key: string): string => {
    return translations[language][key] || key; // Fallback to key if translation not found
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Header Component
const Header: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const logoUrl = "https://i.pinimg.com/736x/d1/02/f8/d102f8f3d03fd26e123b9ff4ea09dda8.jpg"; // New logo link
  const location = useLocation(); // Get current location
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu visibility

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Function to close mobile menu (e.g., when a link is clicked)
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-gray-100 py-3 px-4 md:px-8 shadow-sm relative z-50"> {/* Added relative and z-50 */}
      <div className="container mx-auto flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <img
            src={logoUrl}
            alt="RV Thaifish Logo"
            className="w-12 h-12 mr-2 rounded-full object-cover" // Adjusted size and added styling
            onError={(e) => {
              (e.target as HTMLImageElement).onerror = null; // prevents infinite loop
              (e.target as HTMLImageElement).src = "https://placehold.co/48x48/4CAF50/FFFFFF?text=RV"; // Fallback image with green background
            }}
          />
          <span className="text-xl text-gray-800">{t('rvWebsiteTitle')}</span>
        </div>

        {/* Hamburger Icon for Mobile */}
        <div className="md:hidden flex items-center">
          <button onClick={toggleMobileMenu} className="p-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Main Nav and Language Buttons for Desktop */}
        <div className="hidden md:flex items-center space-x-4">
          <nav className="flex space-x-4 items-center">
            <Link
              to="/"
              className={`font-medium ${location.pathname === '/' ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
            >
              {t('home')}
            </Link>
            <Link
              to="/menu"
              className={`font-medium ${location.pathname === '/menu' ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
            >
              {t('menu')}
            </Link>
            <Link
              to="/contact"
              className={`font-medium ${location.pathname === '/contact' ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
            >
              {t('contact')}
            </Link>
          </nav>
          {/* Language Buttons for Desktop */}
          <div className="flex items-center ml-4 space-x-2">
            <button
              onClick={() => setLanguage('th')}
              className={`font-medium px-3 py-1 rounded-md transition-colors duration-200 ${language === 'th' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              TH
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`font-medium px-3 py-1 rounded-md transition-colors duration-200 ${language === 'en' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown (Conditionally rendered) */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full right-0 w-48 bg-white shadow-lg rounded-b-lg py-2 z-40"> {/* Adjusted z-index */}
          <Link
            to="/"
            className={`block px-4 py-2 text-gray-700 hover:bg-gray-100 ${location.pathname === '/' ? 'text-green-600 font-semibold' : ''}`}
            onClick={closeMobileMenu}
          >
            {t('home')}
          </Link>
          <Link
            to="/menu"
            className={`block px-4 py-2 text-gray-700 hover:bg-gray-100 ${location.pathname === '/menu' ? 'text-green-600 font-semibold' : ''}`}
            onClick={closeMobileMenu}
          >
            {t('menu')}
          </Link>
          <Link
            to="/contact"
            className={`block px-4 py-2 text-gray-700 hover:bg-gray-100 ${location.pathname === '/contact' ? 'text-green-600 font-semibold' : ''}`}
            onClick={closeMobileMenu}
          >
            {t('contact')}
          </Link>
          {/* Language Buttons for Mobile Menu */}
          <div className="flex justify-center items-center mt-2 pt-2 border-t border-gray-200 space-x-2">
            <Globe size={20} className="text-gray-600" />
            <button
              onClick={() => { setLanguage('th'); closeMobileMenu(); }}
              className={`font-medium px-3 py-1 rounded-md transition-colors duration-200 ${language === 'th' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              TH
            </button>
            <button
              onClick={() => { setLanguage('en'); closeMobileMenu(); }}
              className={`font-medium px-3 py-1 rounded-md transition-colors duration-200 ${language === 'en' ? 'bg-green-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              EN
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

// New Contact Info Bar Component
const ContactInfoBar: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="bg-gray-200 py-2 px-4 md:px-8 text-sm">
      <div className="container mx-auto flex flex-col md:flex-row justify-center md:justify-end gap-x-4 gap-y-2 text-gray-600">
        <div className="flex items-center">
          <Phone size={16} className="mr-1             text-green-600" />
          <span>{t('phone')}</span>
        </div>
        <div className="flex items-center">
          <Mail size={16} className="mr-1 text-green-600" />
          <span>{t('email')}</span>
        </div>
        <div className="flex items-center">
          <Facebook size={16} className="mr-1 text-green-600" />
          <span>{t('facebook')}</span>
        </div>
        <div className="flex items-center">
          <MessageCircle size={16} className="mr-1 text-green-600" />
          <span>{t('whatsapp')}</span>
        </div>
      </div>
    </div>
  );
};


// Hero Section Component
const Hero: React.FC = () => {
  const { t } = useLanguage();
  const imageUrl = "https://cache-igetweb-v2.mt108.info/uploads/images-cache/13188/filemanager/752e333b5ed4fe24c818c993102b5a42_full.jpg";
  return (
    <section
      className="relative bg-cover bg-center h-96 md:h-[500px] flex items-center justify-center md:justify-start text-white p-4"
      style={{ backgroundImage: `url(${imageUrl})`} }
    >
      {/* Overlay to darken background image for better text readability */}
      <div className="absolute inset-0 bg-black opacity-50"></div>

      {/* Content Box */}
      <div className="relative z-10 bg-[rgb(80_120_94_/_70%)] text-white p-6 md:p-8 rounded-lg shadow-lg max-w-md md:max-w-xl mx-auto md:mx-0 md:ml-24 lg:ml-32 text-center md:text-left"> {/* Adjusted ml for md and lg screens */}
        <h1 className="text-3xl md:text-5xl font-bold mb-6">
          {t('rvWebsiteTitle')}
        </h1>
        <p className="text-lg md:text-xl font-medium mb-4">
          {t('byRvImportExport')}
        </p>
        <p className="text-sm md:text-base mb-6 leading-relaxed">
          {t('ornamentalFishExporter')}
        </p>
        <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-full transition duration-300 ease-in-out shadow-md">
          {t('readMore')}
        </button>
      </div>
    </section>
  );
};

// Fish Card Component
interface FishCardProps {
  imageSrc: string;
  title: string;
  description: string;
  price: string;
  oldPrice?: string;
  slug: string; // Added slug prop for navigation
}

const FishCard: React.FC<FishCardProps> = ({ imageSrc, title, description, price, oldPrice, slug }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleImageClick = () => {
    navigate(`/fish/${slug}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 border border-gray-200 flex flex-col">
      {/* Card Header */}
      <div className="flex justify-between items-center px-5 pt-5 pb-3">
        <h3 className="text-xl font-bold text-gray-800">{title}</h3>
        <div className="flex space-x-2">
          <Leaf size={20} className="text-green-500" />
        </div>
      </div>

      {/* Card Image - made clickable and added hover effect */}
      <div className="px-5 pb-5 cursor-pointer" onClick={handleImageClick}>
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-48 object-cover rounded-xl border border-gray-100 transform transition-transform duration-300 relative hover:scale-105 hover:z-10" // Reverted scale to 105
          onError={(e) => {
            (e.target as HTMLImageElement).onerror = null; // prevents infinite loop
            (e.target as HTMLImageElement).src = "https://placehold.co/400x300/CCCCCC/333333?text=Image+Not+Found"; // Fallback image
          }}
        />
      </div>

      {/* Card Body (Description) */}
      <div className="px-5 pb-4 flex-grow">
        <p className="text-gray-700 text-base leading-relaxed">{description}</p>
      </div>

      {/* Card Footer (Price and Button) */}
      <div className="flex justify-between items-center px-5 pt-3 pb-5">
        <div className="flex items-baseline">
          <span className="text-xl font-extrabold text-gray-900">{price}</span>
          {oldPrice && (
            <span className="ml-2 text-sm text-gray-500 line-through">{oldPrice}</span>
          )}
        </div>
        <div className="flex">
          <button className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-1.5 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105 shadow-md">
            {t('addCard')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Fish Card Data
interface FishCardData {
  imageSrc: string;
  title: string;
  description: string;
  longDescription: string; // Added new property for detailed description
  price: string;
  oldPrice?: string;
  type: string; // New property for fish type
  slug: string; // Added slug for unique identification in URL
}

const rawFishCardData: FishCardData[] = [ // Renamed to rawFishCardData
  // Discus Fish Cards - Moved to top as per user request
  {
    imageSrc: "https://i.pinimg.com/736x/d6/16/ee/d616ee77eefd5bbea17d8ac6eb93df20.jpg",
    title: "Discus Pompadour 1",
    description: "Beautiful Discus fish, ready for sale.",
    longDescription: "discusPompadour1Long", // Key for translation
    price: "$20.00",
    type: "Discus",
    slug: "discus-pompadour-1",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/89/62/e5/8962e5ed988cb34da2c2c42ba6557612.jpg",
    title: "Discus Pompadour 2",
    description: "Colorful Discus fish, great for keeping.",
    longDescription: "discusPompadour2Long",
    price: "$22.00",
    type: "Discus",
    slug: "discus-pompadour-2",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/95/b7/8f/95b78fa320d5043fbf81c306a54a8ff1.jpg",
    title: "Discus Pompadour 3",
    description: "Healthy Discus fish, ready for shipment.",
    longDescription: "discusPompadour3Long",
    price: "$24.00",
    type: "Discus",
    slug: "discus-pompadour-3",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/93/9b/60/939b60de409d4f359ed411b148c4710a.jpg",
    title: "Discus Pompadour 4",
    description: "High-quality Discus fish, special price.",
    longDescription: "discusPompadour4Long",
    price: "$21.50",
    type: "Discus",
    slug: "discus-pompadour-4",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/a0/89/9c/a0899cac3c0872f60865e4cd38479d8a.jpg",
    title: "Discus Pompadour 5",
    description: "Premium quality Discus fish.",
    longDescription: "discusPompadour5Long",
    price: "$23.00",
    type: "Discus",
    slug: "discus-pompadour-5",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/18/b8/e4/18b8e4a1ca5c4a483803a0fcf50b5756.jpg",
    title: "Discus Pompadour 6",
    description: "Rare Discus fish species.",
    longDescription: "discusPompadour6Long",
    price: "$27.00",
    type: "Discus",
    slug: "discus-pompadour-6",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/c1/31/a7/c131a7948d6f535f138fc06e642c7982.jpg",
    title: "Discus Pompadour 7",
    description: "Vibrant Discus fish.",
    longDescription: "discusPompadour7Long",
    price: "$20.50",
    type: "Discus",
    slug: "discus-pompadour-7",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/e6/e8/85/e6e885d5861824033c4a635d425da821.jpg",
    title: "Discus Pompadour 8",
    description: "Easy-to-care Discus fish.",
    longDescription: "discusPompadour8Long",
    price: "$19.90",
    type: "Discus",
    slug: "discus-pompadour-8",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/16/19/3b/16193b346f3f0adf08b9d980db5e22c6.jpg",
    title: "Discus Pompadour 9",
    description: "Export-quality Discus fish.",
    longDescription: "discusPompadour9Long",
    price: "$25.00",
    type: "Discus",
    slug: "discus-pompadour-9",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/24/d1/45/24d1454ea1e04ff56ff275a73787031d.jpg",
    title: "Discus Pompadour 10",
    description: "Discus fish for collectors.",
    longDescription: "discusPompadour10Long",
    price: "$29.90",
    type: "Discus",
    slug: "discus-pompadour-10",
  },
  // Gold Fish cards
  {
    imageSrc: "https://i.pinimg.com/736x/66/d7/da/66d7da98d7bc9b494895293995c71e16.jpg",
    title: "Oranda Gold Fish 1",
    description: "Orange Oranda gold fish with prominent headgrowth, cute and docile.",
    longDescription: "orandaGoldFish1Long",
    price: "$10.50",
    type: "Gold Fish",
    slug: "oranda-gold-fish-1",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/7f/79/c1/7f79c1795a8c8ff121963556d3888913.jpg",
    title: "Oranda Gold Fish 2",
    description: "Radiant golden Oranda gold fish, swimming gracefully.",
    longDescription: "orandaGoldFish2Long",
    price: "$11.00",
    type: "Gold Fish",
    slug: "oranda-gold-fish-2",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/e0/49/69/e04969a6f2de652d4c5c212e7c19440a.jpg",
    title: "Oranda Gold Fish 3",
    description: "Beautifully patterned Oranda gold fish with unique characteristics.",
    longDescription: "orandaGoldFish3Long",
    price: "$12.00",
    type: "Gold Fish",
    slug: "oranda-gold-fish-3",
  },
  // Betta Fish Cards
  {
    imageSrc: "https://i.pinimg.com/736x/0b/d9/a6/0bd9a672cf063afc33ae3fefa7e673f2.jpg",
    title: "Betta Splendens 1",
    description: "Vibrant Betta fish with flowing fins, ideal for single tanks.",
    longDescription: "bettaSplendens1Long",
    price: "$14.50",
    type: "Betta",
    slug: "betta-splendens-1",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/1c/e0/99/1ce09931ca045fed07b76d31bd43d166.jpg",
    title: "Betta Splendens 2",
    description: "Graceful Betta fish with striking blue and red colors.",
    longDescription: "bettaSplendens2Long",
    price: "$15.00",
    type: "Betta",
    slug: "betta-splendens-2",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/a7/2c/fb/a72cfb309247d5ffc640afd28c82f4f6.jpg",
    title: "Betta Splendens 3",
    description: "Elegant Betta fish, a perfect centerpiece for your aquarium.",
    longDescription: "bettaSplendens3Long",
    price: "$13.00",
    type: "Betta",
    slug: "betta-splendens-3",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/68/39/48/683948e16995fba0264b899a4573483b.jpg",
    title: "Betta Splendens 4",
    description: "Beautifully patterned Betta fish with unique finnage.",
    longDescription: "bettaSplendens4Long",
    price: "$16.00",
    type: "Betta",
    slug: "betta-splendens-4",
  },
  // Angel Fish Cards
  {
    imageSrc: "https://i.pinimg.com/736x/d1/95/39/d1953902fb08ba485ef30b017bb72bd5.jpg",
    title: "Angel Fish 1",
    description: "Graceful Angel Fish with elegant fins, perfect for planted tanks.",
    longDescription: "angelFish1Long",
    price: "$9.50",
    type: "Angel Fish",
    slug: "angel-fish-1",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/56/fd/4d/56fd4d3da2f52d9a1469bd1d899ed76c.jpg",
    title: "Angel Fish 2",
    description: "Stunning Angel Fish, adds serenity to any aquarium.",
    longDescription: "angelFish2Long",
    price: "$10.00",
    type: "Angel Fish",
    slug: "angel-fish-2",
  },
  {
    imageSrc: "https://i.pinimg.com/736x/3b/a6/b0/3ba6b00078a8a943fece1101682ca762.jpg",
    title: "Angel Fish 3",
    description: "Beautifully striped Angel Fish, a classic ornamental fish.",
    longDescription: "angelFish3Long",
    price: "$8.75",
    type: "Angel Fish",
    slug: "angel-fish-3",
  },
];

// Reorder fishCardData to start with Discus, then Gold Fish, Betta, and Angel Fish
const fishCardData: FishCardData[] = [
  ...rawFishCardData.filter(fish => fish.type === "Discus"),
  ...rawFishCardData.filter(fish => fish.type === "Gold Fish"),
  ...rawFishCardData.filter(fish => fish.type === "Betta"),
  ...rawFishCardData.filter(fish => fish.type === "Angel Fish"),
];

interface FishCardsSectionProps {
  initialShowAll?: boolean; // Controls if "View More" button is shown initially (used only on homepage, not for filtered views)
}

const FishCardsSection: React.FC<FishCardsSectionProps> = ({ initialShowAll = false }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('All'); // 'All', 'Discus', 'Gold Fish', 'Betta', 'Angel Fish'
  const [visibleCards, setVisibleCards] = useState(initialShowAll ? fishCardData.length : 6); // Shows 6 on homepage, all on menu page

  // Filter data based on activeTab state
  const filteredData = activeTab === 'All'
    ? fishCardData
    : fishCardData.filter(card => card.type === activeTab);

  useEffect(() => {
    // When the active tab changes, adjust visible cards based on initialShowAll prop
    setVisibleCards(initialShowAll ? filteredData.length : 6);
  }, [activeTab, filteredData.length, initialShowAll]); // Added initialShowAll to dependencies

  const navigate = useNavigate(); // Get navigate function

  const handleViewMore = () => {
    navigate('/menu'); // Navigate to the menu page
  };

  return (
    <section className="py-12 px-4 md:px-8 container mx-auto my-8">
      <div className="container mx-auto">
        {/* Main Title of the section */}
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-8">
          {t('fishListImportExport')}
        </h2>

        {/* Tabs for Fish Types - removed icons and animations, reverted to basic styling */}
        <div className="flex justify-end space-x-4 mb-8 w-full overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setActiveTab('All')}
            className={`py-2 px-6 rounded-full font-semibold whitespace-nowrap ${ // Added whitespace-nowrap
              activeTab === 'All'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('allFish')}
          </button>
          <button
            onClick={() => setActiveTab('Discus')}
            className={`py-2 px-6 rounded-full font-semibold whitespace-nowrap ${ // Added whitespace-nowrap
              activeTab === 'Discus'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('discus')}
          </button>
          <button
            onClick={() => setActiveTab('Gold Fish')}
            className={`py-2 px-6 rounded-full font-semibold whitespace-nowrap ${ // Added whitespace-nowrap
              activeTab === 'Gold Fish'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('goldFish')}
          </button>
          {/* Betta Fish Tab */}
          <button
            onClick={() => setActiveTab('Betta')}
            className={`py-2 px-6 rounded-full font-semibold whitespace-nowrap ${ // Added whitespace-nowrap
              activeTab === 'Betta'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('bettaFish')}
          </button>
          <button
            onClick={() => setActiveTab('Angel Fish')}
            className={`py-2 px-6 rounded-full font-semibold whitespace-nowrap ${ // Added whitespace-nowrap
              activeTab === 'Angel Fish'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t('angelFish')}
          </button>
        </div>

        {/* Grid for Fish Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredData.slice(0, visibleCards).map((card, index) => (
            <FishCard
              key={index}
              imageSrc={card.imageSrc}
              title={card.title}
              description={card.description}
              price={card.price}
              oldPrice={card.oldPrice}
              slug={card.slug} // Pass slug to FishCard
            />
          ))}
        </div>

        {/* "View More..." Button (only shown on Home page for "All" tab if not all cards are visible) */}
        {activeTab === 'All' && !initialShowAll && visibleCards < fishCardData.length && (
          <div className="text-center mt-10">
            <button
              onClick={handleViewMore}
              className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 px-8 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
            >
              {t('viewMore')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// New Shipping Section Component
const ShippingSection: React.FC = () => {
  const { t } = useLanguage();
  const shippingImages = [
    "https://scontent.fbkk5-7.fna.fbcdn.net/v/t39.30808-6/473142545_1014324494049435_1013578329096963249_n.jpg?_nc_cat=107&ccb=1-7&_nc_sid=833d8c&_nc_eui2=AeGDHOb65iP3LXZ8ASnvW2ezmdnltE_OFGaZ2eW0T84UZvwENEZWAL-62Oc28iNF03NWTxzZRchFvtS3HDtkAKUn&_nc_ohc=Pyz3CZNSy4cQ7kNvwGv2gqs&_nc_oc=AdlsRhaesXg1g49f-bgPRwBq0Gud2xpE68Qv_D5EadS8_0kxVl_w5YepgEq1idsQCiFfzdwZqpSPznCgvvGOsom0&_nc_zt=23&_nc_ht=scontent.fbkk5-7.fna&_nc_gid=UbTpZPdxnSDYJLMSWev_og&oh=00_AfMs2Mtm0v1mZy7fKgt3Kfe36DjKMtJN1aWX4hA0sm1ryA&oe=685DFDB6",
    "https://scontent.fbkk5-3.fna.fbcdn.net/v/t39.30808-6/480774498_1042154637933087_7744667373574652991_n.jpg?_nc_cat=111&ccb=1-7&_nc_sid=833d8c&_nc_eui2=AeEG81Vt0-0cDjFvzr9EYCrz0VI-nqXCj6_RUj6epcKPr6lfpq4xd1hPgcW6NTLBCx8r8F-a-7jCOKZpIWxdubQq&_nc_ohc=S6fWl6OwcWIQ7kNvwFvc38L&_nc_oc=Adk7ig1ph1ncNpoOGH4Or5AOuMbQxgkync39mbEhrWZ03YdDvF3KvXv1s4gjsbg5ZoZ60fLFjAV4WIv4UN7QPv6R&_nc_zt=23&_nc_ht=scontent.fbkk5-3.fna&_nc_gid=ZdZpNPVtHA2ISqSgDAcUqQ&oh=00_AfPfR-Mv_FMZRXCD0ec2BUX3eRf0pyLAhJzx6gInv8nMTw&oe=685E16F4",
    "https://scontent.fbkk5-6.fna.fbcdn.net/v/t39.30808-6/470181467_993188852829666_8090024755862856960_n.jpg?_nc_cat=102&ccb=1-7&_nc_sid=833d8c&_nc_eui2=AeG-Cpssa01v5p6y7a8XpId3TYztLWGR1w5NjO0tYZHXDi15nTo0ajmhgWpuwfupQMLsa9NC1l-Cg1WuzBqTIvOk&_nc_ohc=u1W_mXG8r50Q7kNvwGPtGge&_nc_oc=Adnq56L9q_744otnM3XQ6ziXhR8Fv73NrSBXMsDQhTwTQUz6K23Ib5J3GUn-sM38H4VC363-iw5tj6nyQR8T4I6a&_nc_zt=23&_nc_ht=scontent.fbkk5-6.fna&_nc_gid=GqrWtx_TIF2k38tgugKTNQ&oh=00_AfPQvI0jDlfLIUbwn4Q-FiNBdKEq_Kw67jT2H1waytZycg&oe=685DE620", // Updated link for the third image
    "https://scontent.fbkk5-7.fna.fbcdn.net/v/t39.30808-6/470196484_993886956093189_3879964762795663893_n.jpg?_nc_cat=108&ccb=1-7&_nc_sid=833d8c&_nc_eui2=AeF_nCzm8uJyIo5e0cgct5wOyfuoB7PifBDJ-6gHs-J8EKgNpI-tK0afta-g5A4p1radpcorPD9_19Udz1ef43qK&_nc_ohc=zpjuR2Elg60Q7kNvwGujb_G&_nc_oc=Admxw3b4HtfuNT-tFhilQr0b0uFAhq96eXH5PovyL3bfrUnlGFB3EcRRNa8_sqxKDCwD_U5f5juOkhIfE67emUoY&_nc_zt=23&_nc_ht=scontent.fbkk5-7.fna&_nc_gid=BYtCz7PwrTw869GRWsUcyA&oh=00_AfPm_W8qrLXQ_HwRYy32CXKbXjHx7T8W_8Yix-Avia2-mQ&oe=685DFDC6",
  ];

  return (
    <section className="py-12 px-4 md:px-8 container mx-auto bg-gray-50 rounded-lg shadow-md my-8">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
          {t('globalShippingExportServices')}
        </h2>
        <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed">
          {t('shippingDescription')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 justify-items-center"> {/* Adjusted gap and added justify-items-center */}
          {shippingImages.map((src, index) => (
            <div key={index} className="relative w-full aspect-w-4 aspect-h-3 group"> {/* Using aspect ratio classes for consistent sizing */}
              <img
                src={src}
                alt={`Shipping image ${index + 1}`}
                // Removed group-hover:border-green-400
                className="w-full h-full object-cover rounded-xl shadow-lg transition-transform duration-300 transform group-hover:scale-105 border-4 border-transparent"
                onError={(e) => {
                  (e.target as HTMLImageElement).onerror = null;
                  (e.target as HTMLImageElement).src = `https://placehold.co/400x300/A3E4D7/2E8B57?text=Shipping+Image+${index + 1}`;
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Placeholder About Page Component
const AboutPage: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="container mx-auto px-4 py-12 text-center">
      <h2 className="text-4xl font-bold text-gray-800 mb-6">{t('aboutUs')}</h2>
      <p className="text-lg text-gray-700 leading-relaxed">
        {t('aboutUsDescription')}
      </p>
      <img src="https://placehold.co/800x400/A3E4D7/2E8B57?text=About+Us+Image" alt="About Us" className="mx-auto mt-8 rounded-lg shadow-md" />
    </div>
  );
};

// Placeholder Contact Page Component
const ContactPage: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="container mx-auto px-4 py-12 text-center">
      <h2 className="text-4xl font-bold text-gray-800 mt-6 mb-10">{t('contactUs')}</h2>
      <ContactLocationSection />
    </div>
  );
};

// New Contact and Location Section Component
const ContactLocationSection: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section className="bg-white py-12 px-4 md:px-8">
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {/* Left Side: Contact Information */}
        {/* Ensured text-left for the entire content block */}
        <div className="flex flex-col justify-between h-full text-left">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">RV Thai Fish</h2>
            <p className="text-lg text-gray-700 mb-2">{t('addressLine1')}</p>
            <p className="text-lg text-gray-700 mb-4">{t('addressLine2')}</p>
            <p className="text-lg text-gray-700 mb-2">{t('companyName')}</p>
            <p className="text-lg text-gray-700 mb-6">{t('email')}</p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('followUs')}</h3>
            <div className="flex flex-col space-y-2"> {/* Changed to flex-col and space-y-2 for vertical stacking */}
              <a href="tel:+66890685262" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <Phone size={24} className="mr-2 text-green-600" />
                <span>{t('phone')}</span>
              </a>
              <a href="mailto:rv.export.co@gmail.com" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <Mail size={24} className="mr-2 text-green-600" />
                <span>{t('email')}</span>
              </a>
              <a href="https://www.facebook.com/rv.thaifish" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <Facebook size={24} className="mr-2 text-green-600" />
                <span>{t('facebook')}</span>
              </a>
              <a href="https://wa.me/66890685262" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <MessageCircle size={24} className="mr-2 text-green-600" />
                <span>{t('whatsapp')}</span>
              </a>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-8">&copy; {new Date().getFullYear()} {t('companyName')}. {t('allRightsReserved')}.</p>
        </div>

        {/* Right Side: Google Map */}
        <div className="w-full h-[400px] md:h-[500px] bg-gray-200 rounded-lg overflow-hidden shadow-md">
          <iframe
            src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3862.016335198889!2d100.78939227488339!3d14.031105986383375!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x311d79082523bd8f%3A0xa9ad2a4cdc2c637b!2sRV%20Thai%20Fish%20(%20R.V.%20Import%20%26%20Export%20Co.%2CLtd.%20)!5e0!3m2!1sen!2sth!4v${Date.now()}!5m2!1sen!2sth`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen={true}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RV Thaifish Location"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

// New Fish Detail Page Component
const FishDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fish = fishCardData.find(f => f.slug === slug);

  if (!fish) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-4xl font-bold text-gray-800 mb-6">{t('fishNotFound')}</h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          {t('fishNotFoundMessage')}
        </p>
        <button
          onClick={() => navigate('/menu')}
          className="mt-8 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-full transition duration-300 ease-in-out shadow-md"
        >
          {t('backToMenu')}
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <button
        onClick={() => navigate('/menu')}
        className="mb-8 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-full transition duration-300 ease-in-out shadow-md"
      >
        &larr; {t('backToMenu')}
      </button>
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
        {/* Left Section: Image */}
        <div className="w-full">
          <img
            src={fish.imageSrc}
            alt={fish.title}
            className="w-full h-80 object-cover rounded-xl shadow-lg border border-gray-100"
            onError={(e) => {
              (e.target as HTMLImageElement).onerror = null;
              (e.target as HTMLImageElement).src = "https://placehold.co/600x400/CCCCCC/333333?text=Image+Not+Found";
            }}
          />
        </div>

        {/* Right Section: Details */}
        <div className="flex flex-col justify-between h-full">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{fish.title}</h1>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">{t(fish.longDescription)}</p> {/* Display longDescription */}
            <div className="flex items-baseline mb-6">
              <span className="text-3xl font-extrabold text-gray-900">{fish.price}</span>
              {fish.oldPrice && (
                <span className="ml-3 text-xl text-gray-500 line-through">{fish.oldPrice}</span>
              )}
            </div>
          </div>
          <button className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 px-8 rounded-full transition duration-300 ease-in-out transform hover:scale-105 shadow-lg mt-auto">
            {t('addCard')}
          </button>
        </div>
      </div>
    </div>
  );
};


// Home Page Component
const HomePage: React.FC = () => {
  return (
    <>
      <Hero />
      <ShippingSection /> {/* New Shipping Section added here */}
      <FishCardsSection initialShowAll={false} /> {/* Changed to false for homepage */}
      <ContactLocationSection />
    </>
  );
};


// Menu Page Component (re-uses FishCardsSection)
const MenuPage: React.FC = () => {
  return (
    <div className="container mx-auto px-4">
      <FishCardsSection initialShowAll={true} /> {/* Changed to true for menu page */}
    </div>
  );
};

// Footer Component
const Footer: React.FC = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-gray-800 text-white py-6 text-center"> {/* Removed fixed positioning classes */}
      <div className="container mx-auto">
        <p>&copy; {new Date().getFullYear()} {t('companyName')}. {t('allRightsReserved')}.</p>
      </div>
    </footer>
  );
};


// Main App Content Component (wraps the main layout and handles scrolling)
const AppContent: React.FC = () => {
  const location = useLocation(); // Hook to access the current location

  // Effect to scroll to the top of the page on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]); // Dependency array: triggers on pathname changes

  return (
    // Removed pb-20 to allow footer to sit naturally without extra padding if not fixed
    <div className="font-sans antialiased bg-gray-100 min-h-screen">
      <Header />
      <ContactInfoBar />

      {/* Define Routes */}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/fish/:slug" element={<FishDetailPage />} /> {/* New route for fish details */}
      </Routes>

      <Footer />
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <Router>
      <script src="https://cdn.tailwindcss.com"></script>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <LanguageProvider> {/* Wrap AppContent with LanguageProvider */}
        <AppContent /> {/* Render the new AppContent component inside the Router */}
      </LanguageProvider>
    </Router>
  );
};

export default App;
